import { Check, ExternalLink, GitMerge, Plus, Search, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { fetchJson, sendJson } from "../api/rest";
import {
	btn,
	Empty,
	ErrorBanner,
	fmtDateTime,
	fmtNumber,
	input,
	kr,
	Panel,
	Spinner,
} from "../components/flows/ui";
import {
	type LineMember,
	type LineStatus,
	lineUrl,
	type ProductLineList,
	type ProductLineRow,
	type Result,
} from "../types/product-lines";

type Tab = LineStatus | "all";

const TABS: { key: Tab; label: string }[] = [
	{ key: "proposed", label: "Proposed" },
	{ key: "active", label: "Active" },
	{ key: "retired", label: "Retired" },
	{ key: "all", label: "All" },
];

const STATUS_STYLE: Record<LineStatus, string> = {
	proposed: "bg-amber-100 text-amber-800",
	active: "bg-emerald-100 text-emerald-800",
	retired: "bg-slate-100 text-slate-600",
};

const indexable = (line: ProductLineRow) =>
	line.offerMemberCount >= 2 && line.shopCount >= 2;

const ruleText = (models: string[][]) =>
	models.map((m) => m.join(" ")).join("\n");

const check = (res: Result) => {
	if (!res.success) throw new Error(res.error);
};

export default function ProductLines() {
	const [tab, setTab] = useState<Tab>("proposed");
	const [q, setQ] = useState("");
	const [query, setQuery] = useState("");
	const [page, setPage] = useState(1);
	const [data, setData] = useState<ProductLineList | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [openId, setOpenId] = useState<number | null>(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const params = new URLSearchParams({ page: String(page), limit: "50" });
			if (tab !== "all") params.set("status", tab);
			if (query) params.set("q", query);
			setData(await fetchJson<ProductLineList>(`/product-lines?${params}`));
		} catch (err) {
			setError((err as Error).message);
		} finally {
			setLoading(false);
		}
	}, [tab, query, page]);

	useEffect(() => {
		load();
	}, [load]);

	const act = async (fn: () => Promise<Result>) => {
		setError(null);
		try {
			check(await fn());
			await load();
		} catch (err) {
			setError((err as Error).message);
		}
	};

	const setStatus = (line: ProductLineRow, status: LineStatus) =>
		act(() =>
			sendJson<Result>(`/product-lines/${line.id}`, "PATCH", { status }),
		);

	return (
		<div className="space-y-6">
			<div>
				<h1 className="text-xl font-semibold text-slate-800">Product lines</h1>
				<p className="max-w-3xl text-[13px] text-slate-500">
					One page per product line at /produkter/&lt;slug&gt;. The nightly
					build_product_lines job proposes lines and attaches products by rule.
					A line goes live only when it is approved here, and it is indexed once
					two variants have offers at two or more shops.
				</p>
			</div>

			<div className="flex flex-wrap items-center gap-3">
				<div className="flex gap-1 rounded-md border border-hairline bg-white p-1">
					{TABS.map((t) => (
						<button
							key={t.key}
							type="button"
							onClick={() => {
								setTab(t.key);
								setPage(1);
							}}
							className={`rounded px-3 py-1.5 text-[13px] ${
								tab === t.key
									? "bg-primary text-white"
									: "text-nav hover:bg-canvas"
							}`}
						>
							{t.label}
							{t.key !== "all" && data
								? ` (${fmtNumber(data.counts[t.key])})`
								: ""}
						</button>
					))}
				</div>
				<form
					className="flex items-center gap-2"
					onSubmit={(e) => {
						e.preventDefault();
						setPage(1);
						setQuery(q.trim());
					}}
				>
					<input
						className={`${input} w-64`}
						placeholder="Name, slug or keyword"
						value={q}
						onChange={(e) => setQ(e.target.value)}
					/>
					<button type="submit" className={btn.ghost}>
						<Search className="h-4 w-4" />
						Search
					</button>
				</form>
				{loading && <Spinner />}
			</div>

			<ErrorBanner error={error} />

			<Panel>
				{data && data.items.length === 0 ? (
					<Empty>No product lines here.</Empty>
				) : (
					<div className="overflow-x-auto">
						<table className="w-full text-[13px]">
							<thead>
								<tr className="border-b border-hairline text-left text-[11px] uppercase tracking-wide text-slate-500">
									<th className="py-2 pr-4 font-medium">Line</th>
									<th className="py-2 pr-4 font-medium">Source</th>
									<th className="py-2 pr-4 font-medium">Category</th>
									<th className="py-2 pl-3 text-right font-medium">Variants</th>
									<th className="py-2 pl-3 text-right font-medium">Shops</th>
									<th className="py-2 pl-3 text-right font-medium">
										Price span
									</th>
									<th className="py-2 pl-3 font-medium">Status</th>
									<th className="py-2 pl-3 text-right font-medium">Actions</th>
								</tr>
							</thead>
							<tbody>
								{data?.items.map((line) => (
									<LineRows
										key={line.id}
										line={line}
										open={openId === line.id}
										onToggle={() =>
											setOpenId(openId === line.id ? null : line.id)
										}
										onStatus={(status) => setStatus(line, status)}
										onChanged={load}
										onError={setError}
									/>
								))}
							</tbody>
						</table>
					</div>
				)}
				{data && data.totalPages > 1 && (
					<div className="mt-4 flex items-center justify-end gap-2 text-[12px] text-slate-500">
						<button
							type="button"
							className={btn.ghost}
							disabled={page <= 1}
							onClick={() => setPage(page - 1)}
						>
							Previous
						</button>
						<span>
							Page {data.page} of {data.totalPages}
						</span>
						<button
							type="button"
							className={btn.ghost}
							disabled={page >= data.totalPages}
							onClick={() => setPage(page + 1)}
						>
							Next
						</button>
					</div>
				)}
			</Panel>
		</div>
	);
}

function LineRows({
	line,
	open,
	onToggle,
	onStatus,
	onChanged,
	onError,
}: {
	line: ProductLineRow;
	open: boolean;
	onToggle: () => void;
	onStatus: (status: LineStatus) => void;
	onChanged: () => Promise<void>;
	onError: (message: string | null) => void;
}) {
	return (
		<>
			<tr className="border-b border-hairline align-top">
				<td className="py-2 pr-4">
					<button
						type="button"
						onClick={onToggle}
						className="text-left font-medium text-ink hover:underline"
					>
						{line.name}
					</button>
					<div className="text-[11px] text-slate-400">
						/produkter/{line.slug}
					</div>
				</td>
				<td className="py-2 pr-4 text-slate-600">
					{line.source}
					{line.keyword && (
						<div className="text-[11px] text-slate-400">
							“{line.keyword}”{" "}
							{line.searchVolume ? `· ${fmtNumber(line.searchVolume)}/md` : ""}
						</div>
					)}
				</td>
				<td className="py-2 pr-4 text-slate-600">{line.categoryName ?? "—"}</td>
				<td className="py-2 pl-3 text-right tabular-nums">
					{fmtNumber(line.offerMemberCount)} / {fmtNumber(line.memberCount)}
				</td>
				<td className="py-2 pl-3 text-right tabular-nums">
					{fmtNumber(line.shopCount)}
				</td>
				<td className="py-2 pl-3 text-right tabular-nums">
					{line.minPrice === null
						? "—"
						: `${kr(line.minPrice)} – ${kr(line.maxPrice)}`}
				</td>
				<td className="py-2 pl-3">
					<span
						className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${STATUS_STYLE[line.status]}`}
					>
						{line.status}
					</span>
					{line.status === "active" && !indexable(line) && (
						<div className="text-[11px] text-slate-400">noindex</div>
					)}
					{line.mergedIntoSlug && (
						<div className="text-[11px] text-slate-400">
							→ {line.mergedIntoSlug}
						</div>
					)}
				</td>
				<td className="py-2 pl-3 text-right">
					<div className="inline-flex gap-1.5">
						{line.status !== "active" && (
							<button
								type="button"
								className={btn.primary}
								onClick={() => onStatus("active")}
							>
								<Check className="h-4 w-4" />
								Approve
							</button>
						)}
						{line.status !== "retired" && (
							<button
								type="button"
								className={btn.ghost}
								onClick={() => onStatus("retired")}
							>
								<X className="h-4 w-4" />
								Retire
							</button>
						)}
						{line.status === "active" && (
							<a
								href={lineUrl(line.slug)}
								target="_blank"
								rel="noreferrer"
								className={btn.ghost}
							>
								<ExternalLink className="h-4 w-4" />
							</a>
						)}
					</div>
				</td>
			</tr>
			{open && (
				<tr className="border-b border-hairline bg-canvas/50">
					<td colSpan={8} className="p-4">
						<LineDetail line={line} onChanged={onChanged} onError={onError} />
					</td>
				</tr>
			)}
		</>
	);
}

function LineDetail({
	line,
	onChanged,
	onError,
}: {
	line: ProductLineRow;
	onChanged: () => Promise<void>;
	onError: (message: string | null) => void;
}) {
	const [name, setName] = useState(line.name);
	const [slug, setSlug] = useState(line.slug);
	const [brand, setBrand] = useState(line.matchRule.brand ?? "");
	const [models, setModels] = useState(ruleText(line.matchRule.models));
	const [exclude, setExclude] = useState(line.matchRule.exclude.join(", "));
	const [target, setTarget] = useState("");
	const [productId, setProductId] = useState("");
	const [members, setMembers] = useState<LineMember[] | null>(null);
	const [busy, setBusy] = useState(false);

	const loadMembers = useCallback(async () => {
		try {
			setMembers(
				await fetchJson<LineMember[]>(`/product-lines/${line.id}/members`),
			);
		} catch (err) {
			onError((err as Error).message);
		}
	}, [line.id, onError]);

	useEffect(() => {
		loadMembers();
	}, [loadMembers]);

	const run = async (fn: () => Promise<Result>, refreshMembers = false) => {
		setBusy(true);
		onError(null);
		try {
			check(await fn());
			if (refreshMembers) await loadMembers();
			await onChanged();
		} catch (err) {
			onError((err as Error).message);
		} finally {
			setBusy(false);
		}
	};

	const saveNames = () =>
		run(() =>
			sendJson<Result>(`/product-lines/${line.id}`, "PATCH", { name, slug }),
		);

	const saveRule = () =>
		run(() =>
			sendJson<Result>(`/product-lines/${line.id}`, "PATCH", {
				matchRule: {
					brand: brand.trim() || null,
					models: models
						.split("\n")
						.map((m) => m.trim())
						.filter(Boolean),
					exclude: exclude
						.split(",")
						.map((e) => e.trim())
						.filter(Boolean),
				},
			}),
		);

	const merge = () => {
		const targetId = Number(target);
		if (!Number.isInteger(targetId) || targetId <= 0) {
			onError("Enter the id of the line to merge into");
			return;
		}
		run(
			() =>
				sendJson<Result>(`/product-lines/${line.id}/merge`, "POST", {
					targetId,
				}),
			true,
		);
	};

	const attach = () => {
		const id = Number(productId);
		if (!Number.isInteger(id) || id <= 0) {
			onError("Enter a product id");
			return;
		}
		run(
			() =>
				sendJson<Result>(`/product-lines/${line.id}/members`, "POST", {
					productId: id,
				}),
			true,
		).then(() => setProductId(""));
	};

	const setSource = (member: LineMember, source: "manual" | "excluded") =>
		run(
			() =>
				source === "excluded"
					? sendJson<Result>(
							`/product-lines/${line.id}/members/${member.productId}`,
							"DELETE",
						)
					: sendJson<Result>(`/product-lines/${line.id}/members`, "POST", {
							productId: member.productId,
						}),
			true,
		);

	return (
		<div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
			<div className="space-y-5">
				<div className="text-[12px] text-slate-500">
					Line #{line.id} · created {fmtDateTime(line.createdAt)} · members
					built {fmtDateTime(line.membersBuiltAt)}
					{line.reviewedBy &&
						` · reviewed by ${line.reviewedBy} ${fmtDateTime(line.reviewedAt)}`}
				</div>

				<fieldset className="space-y-2" disabled={busy}>
					<legend className="mb-1 text-[12px] font-semibold text-slate-600">
						Name and address
					</legend>
					<input
						className={input}
						value={name}
						onChange={(e) => setName(e.target.value)}
					/>
					<input
						className={input}
						value={slug}
						onChange={(e) => setSlug(e.target.value)}
					/>
					<button type="button" className={btn.ghost} onClick={saveNames}>
						Save name
					</button>
				</fieldset>

				<fieldset className="space-y-2" disabled={busy}>
					<legend className="mb-1 text-[12px] font-semibold text-slate-600">
						Match rule
					</legend>
					<label className="block text-[11px] text-slate-500">
						Brand (products must carry it, or name it)
						<input
							className={input}
							value={brand}
							onChange={(e) => setBrand(e.target.value)}
						/>
					</label>
					<label className="block text-[11px] text-slate-500">
						Model phrases, one per line. Every word must appear in this order
						near the start of the product name.
						<textarea
							className={`${input} h-20 font-mono`}
							value={models}
							onChange={(e) => setModels(e.target.value)}
						/>
					</label>
					<label className="block text-[11px] text-slate-500">
						Exclude words, comma separated
						<input
							className={input}
							value={exclude}
							onChange={(e) => setExclude(e.target.value)}
						/>
					</label>
					<button type="button" className={btn.ghost} onClick={saveRule}>
						Save rule
					</button>
					<p className="text-[11px] text-slate-400">
						Members follow a new rule on the next build_product_lines run.
					</p>
				</fieldset>

				<fieldset className="space-y-2" disabled={busy}>
					<legend className="mb-1 text-[12px] font-semibold text-slate-600">
						Merge into another line
					</legend>
					<div className="flex gap-2">
						<input
							className={input}
							placeholder="Target line id"
							value={target}
							onChange={(e) => setTarget(e.target.value)}
						/>
						<button type="button" className={btn.danger} onClick={merge}>
							<GitMerge className="h-4 w-4" />
							Merge
						</button>
					</div>
					<p className="text-[11px] text-slate-400">
						This line is retired and redirects to the target, which takes over
						its members and model phrases.
					</p>
				</fieldset>
			</div>

			<div className="space-y-3">
				<div className="flex items-center justify-between gap-3">
					<h4 className="text-[12px] font-semibold text-slate-600">
						Members {members ? `(${members.length})` : ""}
					</h4>
					<div className="flex gap-2">
						<input
							className={`${input} w-40`}
							placeholder="Product id"
							value={productId}
							onChange={(e) => setProductId(e.target.value)}
						/>
						<button
							type="button"
							className={btn.ghost}
							onClick={attach}
							disabled={busy}
						>
							<Plus className="h-4 w-4" />
							Attach
						</button>
					</div>
				</div>
				{!members ? (
					<Spinner />
				) : members.length === 0 ? (
					<Empty>No members yet. The next build run attaches them.</Empty>
				) : (
					<div className="max-h-[420px] overflow-y-auto">
						<table className="w-full text-[12px]">
							<tbody>
								{members.map((m) => (
									<tr
										key={m.productId}
										className={`border-b border-hairline ${m.source === "excluded" ? "text-slate-400 line-through" : ""}`}
									>
										<td className="py-1.5 pr-2 tabular-nums text-slate-400">
											{m.productId}
										</td>
										<td className="py-1.5 pr-2">
											{m.slug ? (
												<a
													href={`https://www.priceonline.dk/p/${encodeURIComponent(m.slug)}`}
													target="_blank"
													rel="noreferrer"
													className="hover:underline"
												>
													{m.name}
												</a>
											) : (
												m.name
											)}
										</td>
										<td className="py-1.5 pr-2 text-slate-500">{m.source}</td>
										<td className="py-1.5 pr-2 text-right tabular-nums">
											{m.shops}
										</td>
										<td className="py-1.5 pr-2 text-right tabular-nums">
											{kr(m.minPrice)}
										</td>
										<td className="py-1.5 text-right">
											{m.source === "excluded" ? (
												<button
													type="button"
													className="text-[11px] text-primary hover:underline"
													onClick={() => setSource(m, "manual")}
												>
													Restore
												</button>
											) : (
												<button
													type="button"
													className="text-[11px] text-danger hover:underline"
													onClick={() => setSource(m, "excluded")}
												>
													Detach
												</button>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
		</div>
	);
}
